import { useEffect } from "react";

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { parse } from "date-fns";
import { receiveCompanyPolicy } from "EmployerApplication/Actions";
import { request, updateEntities } from "EntitiesApi/Actions";
import { companyProfileSchema, companyPolicySchema } from "EntitiesApi/schemas";
import { useDispatch, useSelector } from "react-redux";
import { serializeDate } from "Redux/serializers";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken, { fetchWithCSRFTokenForRequestObject } from "Utils/fetchWithCSRFToken";
import { CompanyApplication } from "Utils/types";

import { formFormatDate } from "Common/forms/utils";
import { receivePlans } from "Common/PlanLoader";
import { appendAuthParams } from "Common/TokenAuth/Utils";
import useAsync from "Common/useAsync";

import { receiveCompanyPolicyId } from "./Actions";
import { api as companyApplicationUrls } from "./urls";

import type { EmployerRenewalCompanyPolicy } from "Utils/types";

export const showLoggedInCompanyPolicy = (include_flags, { onSuccess, ...meta } = {}) => {
  const companyIdToken = companyToken();
  return request(
    {
      url: "/api/v1/employer_application/show_logged_in_company_policy",
      method: "post",
      body: { company_id_token: companyIdToken, include: include_flags },
      responseSchema: companyPolicySchema,
      responseKey: "companyPolicy",
    },

    {
      onSuccess: (response, dispatch, request) => {
        dispatch(receiveCompanyPolicy(response));
        dispatch(receiveCompanyPolicyId(response.companyPolicy.id));
        if (onSuccess) {
          onSuccess(response, dispatch, request);
        }
      },
      ...meta,
    },
  );
};

export const showLoggedInCompanyProfile = ({ onSuccess, ...meta } = {}) => {
  const companyIdToken = companyToken();
  return request(
    {
      url: appendAuthParams("/api/v1/employer_application/show_logged_in_company_profile"),

      method: "post",
      body: { company_id_token: companyIdToken },
      responseSchema: companyProfileSchema,
      responseKey: "companyProfile",
    },

    {
      onSuccess: (response, dispatch, request) => {
        dispatch(updateEntities(response));
        if (onSuccess) {
          onSuccess(response, dispatch, request);
        }
      },
      ...meta,
    },
  );
};

export const saveCheckedOutOnce = (companyIdToken = companyToken()) =>
  fetchWithCSRFToken("/api/v1/employer_application/checked_out_once", {
    method: "POST",
    body: JSON.stringify({ company_id_token: companyIdToken }),
    processData: false,
  });

export const saveAddEmployeesStepComplete = () =>
  fetchWithCSRFToken(companyApplicationUrls.setAddEmployeesStepComplete, {
    method: "POST",
    body: JSON.stringify({ company_id_token: companyToken() }),
  });

export const setDisplayedApplicationReminders = (id, meta = {}) =>
  request(
    {
      url: "/api/v1/employer_application/set_displayed_application_reminders",
      method: "post",
      body: { id },
    },

    meta,
  );

// Update the object that controls which Shopping Tab to first display
// The Quote Page itself updates it when syncing filters
// Apps outside of the quote page instead use this endpoint
export const updateQuotePageNavigation = (recommendation_mode) => {
  const companyIdToken = companyToken() || "";
  return fetchWithCSRFToken(`/small-group/select-plan/update-quote-page-navigation?company_id_token=${companyIdToken}`, {
    method: "POST",
    body: JSON.stringify({ recommendation_mode }),
  });
};

export const companyApplicationApi = createApi({
  reducerPath: "companyApplicationApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  refetchOnMountOrArgChange: true,
  tagTypes: ["CompanyApplication"],
  endpoints: (builder) => ({
    getCurrentCompanyApplication: builder.query<CompanyApplication, void>({
      query: () => {
        const companyIDToken = companyToken();
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return appendAuthParams(`/current-company-application?${params}`);
      },
      providesTags: ["CompanyApplication"],
      transformResponse: (response, _meta, _arg) => ({
        ...response,
        dateEstablishedAt: response.dateEstablishedAt ? formFormatDate(new Date(response.dateEstablishedAt)) : null,
        payrollStartDate: response.payrollStartDate ? formFormatDate(new Date(response.payrollStartDate)) : null,
        coverageStartDateAt: response.coverageStartDateAt ? formFormatDate(new Date(response.coverageStartDateAt)) : null,
        companyProfileId: response.companyProfileId || response.companyProfileID,
        companyIdToken: response.companyIdToken || response.companyIDToken,
        submittedAt: response.submittedAt ? serializeDate(new Date(response.submittedAt)) : null,
        nextCarrierDeadline: response.nextCarrierDeadline ? serializeDate(parse(response.nextCarrierDeadline, "yyyy-MM-dd", new Date())) : null,
        payrollStartCutoffDate: response.payrollStartCutoffDate ? serializeDate(parse(response.payrollStartCutoffDate, "yyyy-MM-dd", new Date())) : null,
      }),
    }),

    updateCompanyApplication: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/company-applications/${id}`,
        method: "PATCH",
        body: patch,
      }),

      invalidatesTags: ["CompanyApplication"],
      transformResponse: (response, _meta, _arg) => ({
        ...response,
        dateEstablishedAt: response.dateEstablishedAt ? formFormatDate(new Date(response.dateEstablishedAt)) : null,
        payrollStartDate: response.payrollStartDate ? formFormatDate(new Date(response.payrollStartDate)) : null,
        coverageStartDateAt: response.coverageStartDateAt ? formFormatDate(new Date(response.coverageStartDateAt)) : null,
        companyProfileId: response.companyProfileId || response.companyProfileID,
        companyIdToken: response.companyIdToken || response.companyIDToken,
        submittedAt: response.submittedAt ? serializeDate(new Date(response.submittedAt)) : null,
        nextCarrierDeadline: response.nextCarrierDeadline ? serializeDate(parse(response.nextCarrierDeadline, "yyyy-MM-dd", new Date())) : null,
      }),
    }),

    getNetsuiteDeductionAccounts: builder.query({
      query: () => appendAuthParams(`partner/netsuite-payroll-account-items`),
    }),

    updateNetsuitedDeductionAccounts: builder.mutation({
      query: (preferences) => ({
        url: appendAuthParams("partner/preferences"),
        method: "PUT",
        body: {
          preferences,
          company_id_token: companyToken(),
        },
      }),
    }),
  }),
});

export const {
  useGetCurrentCompanyApplicationQuery,
  useUpdateCompanyApplicationMutation,
  useUpdateNetsuitedDeductionAccountsMutation,
  useGetNetsuiteDeductionAccountsQuery,
} = companyApplicationApi;

export const companyContributionsApi = createApi({
  reducerPath: "companyContributionsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2/company_policies",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getCompanyContributions: builder.query({
      query: (companyPolicyId) => `${companyPolicyId}/company_contributions`,
    }),
  }),
});

export const { useGetCompanyContributionsQuery } = companyContributionsApi;

export const pendingItemsApi = createApi({
  reducerPath: "pendingItemsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2/company_policies",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  tagTypes: ["PendingItems"],
  endpoints: (builder) => ({
    getPendingItems: builder.query({
      query: (companyPolicyId) => `${companyPolicyId}/pending_items`,
      providesTags: ["PendingItems"],
    }),

    resolvePendingItem: builder.mutation({
      query: ({ companyPolicyId, type, ...body }) => ({
        url: `${companyPolicyId}/pending_items/${type}/resolve`,
        method: "POST",
        body,
      }),

      invalidatesTags: ["PendingItems"],
    }),
  }),
});

export const { useGetPendingItemsQuery, useResolvePendingItemMutation } = pendingItemsApi;

export const employerApplicationDocumentApi = createApi({
  reducerPath: "employerApplicationDocumentApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/employer-application",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  endpoints: (builder) => ({
    syncPartnerDocument: builder.mutation({
      query: ({ companyPolicyId, ...body }) => {
        const companyIDToken = companyToken();
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };
        const params = new URLSearchParams(data).toString();
        return {
          url: `sync-partner-document/${companyPolicyId}`,
          method: "POST",
          body,
          params,
        };
      },
    }),

    syncAllPartnerDocuments: builder.mutation({
      query: ({ companyPolicyId }) => {
        const companyIDToken = companyToken();
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };
        const params = new URLSearchParams(data).toString();

        return {
          url: `sync-all-partner-documents/${companyPolicyId}`,
          method: "POST",
          params,
        };
      },
    }),
  }),
});

export const { useSyncPartnerDocumentMutation, useSyncAllPartnerDocumentsMutation } = employerApplicationDocumentApi;

export const accountApi = createApi({
  reducerPath: "accountApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2/accounts",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  refetchOnMountOrArgChange: true,
  tagTypes: ["Account"],
  endpoints: (builder) => ({
    getCurrentAccount: builder.query({
      query: () => "/current_account_only",
    }),

    createFromDarkAccount: builder.mutation({
      query: ({ ...body }) => ({
        url: "/create-from-dark-account",
        method: "POST",
        body,
      }),

      invalidatesTags: ["Account"],
    }),
  }),
});

export const { useGetCurrentAccountQuery, useCreateFromDarkAccountMutation } = accountApi;

export const formattedEmployeeDataApi = createApi({
  reducerPath: "formattedEmployeeDataApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v1/",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getFormattedEmployeeData: builder.query({
      query: () => {
        const companyIDToken = companyToken();
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return {
          url: appendAuthParams(`employer_application/fetch_formatted_employee_data?${params}`),

          method: "get",
        };
      },
      providesTags: ["FormattedEmployeeData"],
    }),
  }),

  tagTypes: ["FormattedEmployeeData"],
});

export const { useGetFormattedEmployeeDataQuery } = formattedEmployeeDataApi;

export const medicalPlansApi = (planIds, quoteZipCode, companyIdToken) => {
  const apiUrl = "/employer-application/ajax-get-medical-plans";
  const planIdsStr = encodeURIComponent(JSON.stringify(planIds));
  const url = `${apiUrl}?ids=${planIdsStr}&zipcode=${quoteZipCode}&company_id_token=${companyIdToken}`;

  return fetchWithCSRFToken(url, {
    method: "GET",
    processData: false,
  });
};

// This is a very ad hoc hook that is used to fetch medical plans for the cart.
// Currently this is used to load medical plans in the store when the app loads.
export const useMedicalPlansQuery = () => {
  const planIds = useSelector((state) => state?.cart?.medical);
  const relatedCartPlanIds = useSelector((state) => state?.relatedCartPlanIds);
  const { data: { companyIdToken, quoteZipCode } = {} } = useGetCurrentCompanyApplicationQuery();
  const dispatch = useDispatch();

  const loadPlans = async () => {
    const allIds = (planIds || []).concat(relatedCartPlanIds || []).filter((v, i, a) => a.indexOf(v) === i);
    if (allIds.length === 0) return;
    const response = await medicalPlansApi(allIds, quoteZipCode, companyIdToken);

    if (response.status === 200) {
      const { plans } = await response.json();
      // Since this is not an RTK query, we need to manually dispatch the action
      // before this hook sets isLoading to false.
      dispatch(receivePlans(plans));
    }
  };
  const { isLoading, execute: loadMedicalPlans } = useAsync(loadPlans, false);
  useEffect(() => {
    if (companyIdToken && quoteZipCode && planIds && relatedCartPlanIds) {
      loadMedicalPlans();
    }
  }, [planIds, relatedCartPlanIds, companyIdToken, quoteZipCode]);
  return { isLoading };
};

export const userExperienceFlagsApi = createApi({
  reducerPath: "userExperienceFlagsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getUserExperienceFlags: builder.query({
      query: (arg) => {
        const { companyIDToken } = arg;
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return {
          url: appendAuthParams(`/user-experience-flags?${params}`),
          method: "GET",
        };
      },
      providesTags: ["UserExperienceFlags"],
    }),

    updateUserExperienceFlags: builder.mutation({
      query: ({ companyIDToken, ...body }) => {
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return {
          url: appendAuthParams(`/user-experience-flags?${params}`),
          method: "PUT",
          body: JSON.stringify({
            user_experience_flags: body,
          }),
        };
      },
      invalidatesTags: ["UserExperienceFlags"],
    }),
  }),

  tagTypes: ["UserExperienceFlags"],
});

export const { useGetUserExperienceFlagsQuery, useUpdateUserExperienceFlagsMutation } = userExperienceFlagsApi;

export const lifePlansApi = createApi({
  reducerPath: "lifePlansApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  endpoints: (builder) => ({
    getLifePlans: builder.query({
      query: (arg) => {
        const {
          companyIDToken,
          useEmployeeForms,
          companyZipCode: _companyZipCode,
          employeeProfile: _employeeProfile,
          targetEffectiveDate: _targetEffectiveDate,
        } = arg;
        const data = {
          ...(companyIDToken
            ? {
                company_id_token: companyIDToken,
                use_employee_forms: !!useEmployeeForms,
              }
            : {}),
        };

        const params = new URLSearchParams(data).toString();
        return {
          url: appendAuthParams(`/life-plans?${params}`),
          method: "get",
        };
      },
      providesTags: ["LifePlans"],
    }),
  }),

  tagTypes: ["LifePlans"],
});

export const { useGetLifePlansQuery } = lifePlansApi;

export const selectLifePlans = lifePlansApi.endpoints.getLifePlans.select();

export const shortTermDisabilityPlansApi = createApi({
  reducerPath: "shortTermDisabilityPlansApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getShortTermDisabilityPlans: builder.query({
      query: (arg) => {
        const { companyIDToken } = arg;
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return {
          url: appendAuthParams(`/short-term-disability-plans?${params}`),
          method: "get",
        };
      },
      providesTags: ["ShortTermDisabilityPlans"],
    }),
  }),

  tagTypes: ["ShortTermDisabilityPlans"],
});

export const { useGetShortTermDisabilityPlansQuery } = shortTermDisabilityPlansApi;

export const selectShortTermDisabilityPlans = shortTermDisabilityPlansApi.endpoints.getShortTermDisabilityPlans.select();

export const longTermDisabilityPlansApi = createApi({
  reducerPath: "longTermDisabilityPlansApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getLongTermDisabilityPlans: builder.query({
      query: (arg) => {
        const { companyIDToken } = arg;
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return {
          url: appendAuthParams(`/long-term-disability-plans?${params}`),
          method: "get",
        };
      },
      providesTags: ["LongTermDisabilityPlans"],
    }),
  }),

  tagTypes: ["LongTermDisabilityPlans"],
});

export const { useGetLongTermDisabilityPlansQuery } = longTermDisabilityPlansApi;

export const selectLongTermDisabilityPlans = longTermDisabilityPlansApi.endpoints.getLongTermDisabilityPlans.select();

export interface PlanSelectionValidationRequest {
  planId: string;
}
export interface PlanSelectionValidationResponse {
  carrierChange: boolean;
  inStateParticipation: {
    valid: boolean;
    requirement: number;
    enrolleesOnly: boolean;
    state: string;
  };
}

export const enrolledPolicyPlansApi = createApi({
  reducerPath: "enrolledPolicyPlansApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  endpoints: (builder) => ({
    getEnrolledPolicyPlans: builder.query({
      query: (arg) => {
        const { companyIDToken } = arg;
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return {
          url: appendAuthParams(`/api/v2/enrolled-policy-plans?${params}`),
          method: "get",
        };
      },
      providesTags: ["EnrolledPolicyPlans"],
    }),
    validatePlanSelection: builder.mutation<PlanSelectionValidationResponse, PlanSelectionValidationRequest>({
      query: (body) => ({
        url: appendAuthParams(`/api/v1/employer_application/validate_plan_selection`),
        method: "POST",
        body: { ...body, company_id_token: companyToken() },
      }),
    }),
  }),

  tagTypes: ["EnrolledPolicyPlans"],
});

export const { useGetEnrolledPolicyPlansQuery, useValidatePlanSelectionMutation } = enrolledPolicyPlansApi;

export const disabilityQuoteApi = createApi({
  reducerPath: "disabilityQuoteApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    createDisabilityQuote: builder.mutation({
      query: (arg) => {
        const { disabilityPlanID, displayedPrice, companyIDToken } = arg;
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
          ...(disabilityPlanID ? { disability_plan_id: disabilityPlanID } : {}),
          ...(displayedPrice ? { displayed_price: displayedPrice } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return {
          url: appendAuthParams(`/disability-quotes?${params}`),
          method: "POST",
        };
      },
      providesTags: ["DisabilityQuote"],
    }),
  }),

  tagTypes: ["DisabilityQuote"],
});

export const { useCreateDisabilityQuoteMutation } = disabilityQuoteApi;

export const renewalApi = createApi({
  reducerPath: "renewalApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v1/",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),
  tagTypes: ["CompanyPolicy"],
  endpoints: (builder) => ({
    showCompanyPolicy: builder.mutation<EmployerRenewalCompanyPolicy, string>({
      query: (id) => ({
        url: "employer_application/show_company_policy",
        method: "POST",
        body: { id },
      }),
      invalidatesTags: ["CompanyPolicy"],
    }),

    updateCompanyPolicy: builder.mutation<EmployerRenewalCompanyPolicy, Partial<EmployerRenewalCompanyPolicy> & { id: string }>({
      query: (body) => ({
        url: "employer_application/update_company_policy",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CompanyPolicy"],
    }),

    reopenCompanyPolicy: builder.mutation<EmployerRenewalCompanyPolicy, string>({
      query: (id) => ({
        url: "employer_application/reopen_company_renewal",
        method: "POST",
        body: { id },
      }),
      invalidatesTags: ["CompanyPolicy"],
    }),

    activateEmployeeRenewals: builder.mutation<EmployerRenewalCompanyPolicy, Partial<EmployerRenewalCompanyPolicy> & { id: string }>({
      query: (body) => ({
        url: "employer_application/activate_employee_renewals",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CompanyPolicy"],
    }),

    signApplication: builder.mutation<EmployerRenewalCompanyPolicy, Partial<EmployerRenewalCompanyPolicy> & { id: string }>({
      query: (body) => ({
        url: "employer_application/sign_application",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CompanyPolicy"],
    }),

    updateEmployerDocuments: builder.mutation<EmployerRenewalCompanyPolicy, Partial<EmployerRenewalCompanyPolicy> & { id: string }>({
      query: (body) => ({
        url: "/employer-application/documents/renewal-upload",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CompanyPolicy"],
    }),
  }),
});

export const {
  useShowCompanyPolicyMutation,
  useUpdateCompanyPolicyMutation,
  useReopenCompanyPolicyMutation,
  useActivateEmployeeRenewalsMutation,
  useSignApplicationMutation,
  useUpdateEmployerDocumentsMutation,
} = renewalApi;

export const employeeFormsApi = createApi({
  reducerPath: "employeeFormsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v2",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),
  tagTypes: ["EmployeeForm"],
  endpoints: (builder) => ({
    updateEmployeeForm: builder.mutation<any, { id: string | number; context: string; [key: string]: any }>({
      query: ({ id, context, ...values }) => ({
        url: `/employee-forms/${id}`,
        method: "PUT",
        body: {
          context,
          employee_form: { ...values },
        },
      }),
    }),
  }),
});

export const { useUpdateEmployeeFormMutation } = employeeFormsApi;
